/**
 * IA screen 53 — **Integrations.** F01's settings surface, and the one this product kept implying.
 *
 * ── Why this screen exists ────────────────────────────────────────────────────────────────────
 *
 * Everything here already existed, in four places. Connecting was on `Sources`; scheduling was in
 * `SyncPanel`; a read-only register of what was connected was on `settings.`; the credential intake
 * arrived in the setup wizard, which is a journey somebody finishes and never returns to. So a
 * client who wanted to answer *"what is connected, is it working, and how do I fix it?"* had to know
 * which of four screens held which third of the answer — and one of them they could not get back to.
 *
 * ⚠️ **This screen introduces no new capability except one.** It composes what exists, and adds the
 * on-demand connection test (`POST /api/relay/connections/:id/test`), which had no client-facing
 * caller at all: `pingProvider` ran on a six-hourly cron, so the answer to "is it working?" was
 * always up to six hours old and the person who had just fixed something could not ask.
 *
 * ── ⚠️ Every action here is per CONNECTION, not per provider ──────────────────────────────────
 *
 * Two Salesforce orgs are two connections with two independent health states, and a control that
 * silently picked one would test, reconnect or disconnect the wrong system. Sources already learned
 * this for custom-object mapping; this screen inherits the rule rather than rediscovering it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Callout, Card, EmptyState, HelpText, PageHeader, Skeleton } from '@dedupr/ui';
import { ErrorState } from '@dedupr/app-kit';
import { accessToken } from '../auth.js';
import { startConnect } from '../connectFlow.js';
import { discoverSchema, type DiscoveryOutcome } from '../local/schema.js';
import { openLocalStore } from '../local/store.js';
import './Integrations.css';

interface ObjectView {
  id: string;
  display_name: string;
  supports_discovery: boolean;
}

interface ProviderView {
  providerId: string;
  providerName: string;
  transport: 'rest' | 'file';
  can_connect_now: boolean;
  objects?: ObjectView[];
}

interface ConnectionView {
  id: string;
  provider: string;
  connectedAt: number;
  needsReconnect: boolean;
  health?: 'healthy' | 'degraded' | 'auth_error';
  healthCheckedAt?: number;
  /**
   * US-1.3's partial grant: read scopes the provider granted less than, or **`null` when the
   * provider never stated its grant** — which is unknown, not "granted nothing" (B-278).
   *
   * ⚠️ **This screen did not read it, and that split one sentence across two hosts.** `summarise`
   * has computed it since F01-A; `CredentialRegister` on `settings.` renders *"Not granted: … —
   * reconnect to approve"* and has **no reconnect control**; this screen has the **Reauthorise**
   * button and never said why anybody would press it. So a client whose grant went partial — which
   * is what happens to every installed portal the moment we add a required scope — saw a healthy
   * connection here and an instruction they could not act on there.
   */
  missingReadScopes?: string[] | null;
}

interface TestResult {
  health: 'healthy' | 'degraded' | 'auth_error';
  checkedAt: number;
  summary: string;
  remedy: string | null;
}

const TONE = {
  healthy: 'success',
  degraded: 'warning',
  auth_error: 'danger',
} as const;

/**
 * **What fields this system actually has, and what has moved since we last looked.**
 *
 * ⚠️ `POST /api/relay/discover` has existed, tested, since the connector package was written and
 * has had **no client caller at all** — so a client could connect Salesforce and never see a single
 * field name. Drift was computed on the six-hourly sweep and written to the audit trail, where
 * nobody would find it. This is that route's first reader.
 *
 * ⚠️ **A first look reports no drift rather than none.** There is nothing to compare against, and
 * "0 changes" would claim a comparison that never happened.
 */
function SchemaPanel({
  connectionId,
  providerId,
  objects,
}: {
  connectionId: string;
  providerId: string;
  objects: ObjectView[];
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [seen, setSeen] = useState<Record<string, DiscoveryOutcome>>({});
  const [refused, setRefused] = useState<Record<string, string>>({});

  /**
   * ⚠️ **The persisted schemas had a writer and no reader, and I wrote both halves.**
   *
   * `discoverSchema` stores every field list in IndexedDB, and `listSchemas()` was read in exactly
   * one place — inside `discoverSchema` itself, for the drift baseline. This panel initialised
   * `seen` to `{}` and never loaded anything, so a field list the client had just spent one of
   * their own API calls on vanished the moment they navigated away, and the store provisioned by
   * migration v13 existed to serve its own writer.
   *
   * ⚠️ **`drift: null` on a loaded row is the honest value**, not an empty diff. What was persisted
   * is *what the fields were*; whether they have moved since is only knowable by asking again. A
   * restored `{added: [], removed: []}` would render as "nothing has changed", which is a claim
   * about the system rather than about our record of it.
   */
  useEffect(() => {
    let live = true;
    void (async () => {
      const store = await openLocalStore().catch(() => null);
      if (!store) return;
      try {
        const rows = await store.listSchemas();
        if (!live) return;
        setSeen((current) => {
          const restored = { ...current };
          for (const row of rows) {
            if (row.connection_id !== connectionId) continue;
            /* Never overwrite a discovery made in this session — that one knows its drift. */
            if (restored[row.object_id]) continue;
            restored[row.object_id] = {
              connectionId: row.connection_id,
              objectId: row.object_id,
              provider: row.provider,
              fields: row.fields,
              drift: null,
              comparedAgainst: row.discovered_at,
            };
          }
          return restored;
        });
      } finally {
        store.close();
      }
    })();
    return () => {
      live = false;
    };
  }, [connectionId]);

  /* Only objects whose descriptor declares a discovery spec. The rest are not a failure to report —
     Graph has no describe endpoint for some shapes, and offering a control that always refuses is
     the disabled-button-with-no-explanation this codebase keeps rejecting. */
  const discoverable = objects.filter((o) => o.supports_discovery);
  if (discoverable.length === 0) return null;

  async function check(objectId: string) {
    setBusy(objectId);
    setRefused((r) => ({ ...r, [objectId]: '' }));
    try {
      const result = await discoverSchema(connectionId, providerId, objectId);
      if (result.ok) setSeen((s) => ({ ...s, [objectId]: result.outcome }));
      else setRefused((r) => ({ ...r, [objectId]: result.detail }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="web-integrations__schema">
      <HelpText>
        Read the fields each object actually has, straight from the system. Checking again later
        reports what has been added or removed since — which is how a sync starts missing data
        without anything failing.
      </HelpText>
      <ul className="web-integrations__objects">
        {discoverable.map((object) => {
          const outcome = seen[object.id];
          const problem = refused[object.id];
          return (
            <li key={object.id}>
              <div className="web-integrations__object-head">
                <span>{object.display_name}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy === object.id}
                  onClick={() => void check(object.id)}
                >
                  {busy === object.id ? 'Reading…' : outcome ? 'Check again' : 'Check fields'}
                </Button>
              </div>
              {problem ? (
                <Callout tone="warning" title="That could not be read">
                  {problem}
                </Callout>
              ) : null}
              {outcome ? (
                <>
                  <HelpText>
                    {outcome.fields.length} field{outcome.fields.length === 1 ? '' : 's'}:{' '}
                    {outcome.fields.join(', ')}
                  </HelpText>
                  {/* ⚠️ Three states, not two. `null` is "no previous look", which is why it reads
                      differently from "nothing changed" — collapsing them would tell a first-time
                      reader their schema is stable on the strength of one observation. */}
                  {outcome.drift === null ? (
                    <HelpText>
                      This is the first time we have read this object, so there is nothing to compare
                      against yet. Check again after your next sync and any change will show here.
                    </HelpText>
                  ) : outcome.drift.added.length === 0 && outcome.drift.removed.length === 0 ? (
                    <HelpText>
                      Unchanged since {new Date(outcome.comparedAgainst!).toLocaleString()}.
                    </HelpText>
                  ) : (
                    <Callout tone="warning" title="The fields have changed">
                      {outcome.drift.added.length > 0 ? (
                        <p>Added: {outcome.drift.added.join(', ')}</p>
                      ) : null}
                      {outcome.drift.removed.length > 0 ? (
                        <p>
                          Removed: {outcome.drift.removed.join(', ')} — anything Dedupr was reading
                          from these has stopped arriving.
                        </p>
                      ) : null}
                    </Callout>
                  )}
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      {/* ⚠️ The limit, stated where the answer is — not in a doc. The discovery contract carries no
          types, so a field retyped from text to number is invisible here and would be reported as
          "unchanged". Saying so is the difference between a check and a false assurance. */}
      <HelpText>
        This compares field names and labels only. A field whose <em>type</em> changed — text to
        number, say — looks unchanged here, because the systems do not tell us types.
      </HelpText>
    </div>
  );
}

export function Integrations({ orgId }: { orgId?: string }) {
  const [providers, setProviders] = useState<ProviderView[] | null>(null);
  const [connections, setConnections] = useState<ConnectionView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  /**
   * The outcome of an action taken **on** this screen — kept apart from `error`, which means "the
   * list could not be read" and replaces the whole screen with an `ErrorState`.
   *
   * ⚠️ Conflating them would be a real regression: a refused connect would blank the register of
   * everything already connected, so the reader would lose the working connections they came to
   * look at because a different provider said no.
   */
  const [notice, setNotice] = useState<{
    tone: 'success' | 'warning';
    text: string;
    /**
     * The connection this outcome is ABOUT, when it is about one.
     *
     * ⚠️ **Added 2026-08-17 because "nothing happens when I click Reauthorise" was true from the
     * reader's seat and false in the code** (B-297). The click fired
     * `/api/relay/connect/hubspot?org=…&escalate=conn-hs-1` correctly, the failure was captured, and
     * `setNotice` put it in a Callout **1057px above the viewport** — measured — because the rows sit
     * far down a long page and the notice rendered under the page header. A button that reports its
     * failure where nobody is looking is indistinguishable from a dead one.
     *
     * ⚠️ **And a page-level notice could not say WHICH connection.** "Could not start the connection
     * (404)" above a list holding two Salesforce orgs and a HubSpot names none of them; a client with
     * a real reason ("that portal is not configured on our side") would not know which row it applied
     * to. Targeting is what makes the message answerable rather than merely present.
     */
    target?: string;
  } | null>(null);

  /**
   * Bring a page-level notice into view when it appears.
   *
   * ⚠️ Only the **untargeted** ones: a targeted notice renders inside the row whose button was just
   * pressed, which is already where the reader is looking, and scrolling to it would move the page
   * under them for no reason.
   */
  const noticeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (notice && !notice.target) noticeRef.current?.scrollIntoView({ block: 'center' });
  }, [notice]);

  const load = useCallback(async () => {
    try {
      const token = await accessToken();
      const authed: HeadersInit | undefined = token ? { authorization: `Bearer ${token}` } : undefined;
      const [setup, conns, registry] = await Promise.all([
        fetch('/api/relay/setup').then((r) => r.json()),
        fetch('/api/relay/connections', { headers: authed })
          .then((r) => (r.ok ? r.json() : { connections: [] }))
          .catch(() => ({ connections: [] })),
        /* ⚠️ A third read, because `setup` and `providers` answer different questions and only one
           of them knows about objects. `setup` is "what does this provider need before anyone can
           connect it"; `providers` is the registry — the objects, their fields, and whether each
           declares a discovery spec. Adding objects to `setup` would have been fewer requests and
           two routes describing the same thing, which is how they drift. Allowed to fail on its
           own: an unreadable registry costs the field lists, not the whole screen. */
        fetch('/api/relay/providers')
          .then((r) => (r.ok ? r.json() : { providers: [] }))
          .catch(() => ({ providers: [] })),
      ]);
      const objectsById = new Map<string, ObjectView[]>(
        ((registry as { providers?: { id: string; objects?: ObjectView[] }[] }).providers ?? []).map(
          (p) => [p.id, p.objects ?? []]
        )
      );
      setProviders(
        (setup as { providers: ProviderView[] }).providers.map((p) => ({
          ...p,
          objects: objectsById.get(p.providerId) ?? [],
        }))
      );
      setConnections((conns as { connections: ConnectionView[] }).connections ?? []);
      setError(null);
    } catch {
      setError('The connector list could not be read.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Connect, or re-consent an existing connection in place.
   *
   * ⚠️ The **one** difference between the two is `reconsent` — which carries the existing
   * connection id, so the grant replaces this connection rather than minting a second one beside
   * it. Without it every source stays pointed at the connection whose token was just superseded.
   */
  const begin = useCallback(
    async (providerId: string, reconsent?: string) => {
      setNotice(null);
      const outcome = await startConnect(providerId, { orgId, reconsent });
      /* The Worker's own words. They name whether this is a missing session, no organisation, or an
         unconfigured client secret — three different problems with three different remedies, and a
         generic sentence of ours would discard the only useful part of the answer. */
      /* ⚠️ Targeted when re-consenting an existing connection, so the answer appears in that row
         rather than above a list that does not say which row it meant (B-297). */
      if (!outcome.ok) setNotice({ tone: 'warning', text: outcome.detail, target: reconsent });
    },
    [orgId]
  );

  /**
   * Disconnect: revoke upstream where the provider offers an endpoint, then delete our copy.
   *
   * ⚠️ **A failed upstream revocation still deletes our token, and the response says so** — M1-R4's
   * rule, and the reason this route exists rather than a link to the provider's own console. The
   * one action a worried client takes must never be the one that silently does not happen.
   */
  const disconnect = useCallback(
    async (connectionId: string, providerName: string) => {
      setDisconnecting(connectionId);
      setNotice(null);
      try {
        const token = await accessToken();
        const res = await fetch(`/api/relay/connections/${encodeURIComponent(connectionId)}`, {
          method: 'DELETE',
          headers: token ? { authorization: `Bearer ${token}` } : undefined,
        });
        const body = (await res.json().catch(() => null)) as
          | { revoked?: boolean; detail?: string }
          | null;
        if (!res.ok) {
          setNotice({
            tone: 'warning',
            text: body?.detail ?? `${providerName} could not be disconnected (${res.status}).`,
          });
          return;
        }
        /* Two different outcomes, said apart. "Disconnected" alone would let somebody believe we
           had withdrawn the grant at the provider when we had only dropped our own copy — which is
           precisely the thing they came here to be sure of. */
        setNotice(
          body?.revoked === false
            ? {
                tone: 'warning',
                text: `${providerName} is disconnected here and our copy of the token is deleted. We could not withdraw the grant at ${providerName} itself — do that in their own console to be certain.`,
              }
            : {
                tone: 'success',
                text: `${providerName} is disconnected and the grant was withdrawn at ${providerName} too.`,
              }
        );
        void load();
      } finally {
        setDisconnecting(null);
      }
    },
    [load]
  );

  async function test(connectionId: string) {
    setTesting(connectionId);
    try {
      const token = await accessToken();
      const res = await fetch(`/api/relay/connections/${encodeURIComponent(connectionId)}/test`, {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      });
      const body = (await res.json().catch(() => null)) as (TestResult & { detail?: string }) | null;
      if (!res.ok || !body) {
        setResults((r) => ({
          ...r,
          [connectionId]: {
            health: 'degraded',
            checkedAt: Date.now(),
            /* ⚠️ "We could not check" is NOT "it is broken" — reporting a failed check as an
               auth error would send somebody to reconnect a connection that is fine. */
            summary: body?.detail ?? 'We could not complete the check.',
            remedy: 'This is a problem on our side, not with your connection. Try again shortly.',
          },
        }));
        return;
      }
      setResults((r) => ({ ...r, [connectionId]: body }));
      /* Re-read, so the badge on this page and everywhere else agrees with what was just shown. */
      void load();
    } finally {
      setTesting(null);
    }
  }

  if (error) return <ErrorState code="connector_list" message={error} onRetry={() => void load()} />;
  if (!providers) return <Skeleton />;

  const network = providers.filter((p) => p.transport !== 'file');

  return (
    <div className="web-integrations">
      {/* ⚠️ **This screen had no `h1` at all**, found independently on both branches this landed
          from — it is an unlisted route, so the shell supplies no `PageHeader`, and the document
          began at the intro paragraph. Same defect the setup wizard shipped with, found the same
          way: by looking at it. `description` on `PageHeader` rather than a separate `<p>`, so the
          two branches' fixes don't leave a duplicated heading pattern in the codebase. */}
      <PageHeader
        title="Integrations"
        description="Every system Dedupr can read from, what it is doing right now, and everything you might need to change about it."
      />

      {/* ⚠️ **Untargeted only.** A notice about one connection belongs in that connection's row; see
          the `target` field above. `aria-live` because an outcome that appears without focus moving is
          unannounced to a screen reader for exactly the reason it was invisible to a sighted reader —
          the same defect twice. */}
      {notice && !notice.target ? (
        <div ref={noticeRef} aria-live="polite">
          <Callout
            tone={notice.tone}
            title={notice.tone === 'success' ? 'Done' : 'That did not complete'}
          >
            {notice.text}
          </Callout>
        </div>
      ) : null}

      {connections.length === 0 ? (
        <EmptyState
          title="Nothing is connected yet"
          description="Connect a system below, or add a file on Sources. Read-only access is all any connection asks for."
        />
      ) : null}

      {network.map((provider) => {
        const mine = connections.filter((c) => c.provider === provider.providerId);
        return (
          <Card key={provider.providerId} title={provider.providerName} headingLevel={3}>
            {mine.length === 0 ? (
              <>
                <HelpText>Not connected.</HelpText>
                {provider.can_connect_now ? (
                  <Button onClick={() => void begin(provider.providerId)}>
                    Connect {provider.providerName}
                  </Button>
                ) : (
                  <>
                    <Callout tone="info" title="One step is ours, not yours">
                      We have not finished setting {provider.providerName} up yet — there is nothing
                      for you to create and nothing to send us.
                    </Callout>
                  </>
                )}
              </>
            ) : (
              <ul className="web-integrations__conns">
                {/* ⚠️ One row per CONNECTION. Two Salesforce orgs are two independent things, and a
                    control that picked one silently would act on the wrong system. */}
                {mine.map((conn) => {
                  const result = results[conn.id];
                  const health = result?.health ?? conn.health;
                  return (
                    <li key={conn.id} className="web-integrations__conn">
                      <div className="web-integrations__connhead">
                        <span>
                          Connected {new Date(conn.connectedAt).toLocaleDateString()}
                        </span>
                        {/* ⚠️ **Health and grant are TWO badges, not one slot — and the first version of
                            this made them compete.** `health` says *can we reach it*; `missingReadScopes`
                            says *may we read what we need*. Ranking the grant above the health badge
                            looked tidy and **hid "Not checked yet"** on the one fixture that has it: a
                            connection that is under-granted AND never verified showed only "Limited
                            access", so the reader silently lost the fact that nothing had ever tested it.
                            Caught by opening the screen — every assertion passed, because each test
                            asserted the badge it cared about and none asked what had disappeared. */}
                        {health ? (
                          <Badge variant={TONE[health]}>
                            {health === 'healthy' ? 'Working' : health === 'degraded' ? 'Slow or erroring' : 'Needs reconnecting'}
                          </Badge>
                        ) : (
                          /* ⚠️ Never-checked is its own state, not "healthy". A connection nobody has
                             tested is not a connection known to work. */
                          <Badge variant="neutral">Not checked yet</Badge>
                        )}
                        {/* ⚠️ **Suppressed only for `auth_error`**, where a refused credential means
                            nothing works at all — "Needs reconnecting · Limited access" would offer a
                            smaller problem beside a total one. Otherwise it sits BESIDE the health badge,
                            because "Working · Limited access" is exactly the truth: reachable, and not
                            permitted to read everything. */}
                        {health !== 'auth_error' && conn.missingReadScopes && conn.missingReadScopes.length > 0 ? (
                          <Badge variant="warning">Limited access</Badge>
                        ) : null}
                      </div>

                      {result ? (
                        <Callout
                          tone={result.health === 'healthy' ? 'success' : result.health === 'degraded' ? 'warning' : 'pending'}
                          title={result.summary}
                        >
                          {result.remedy ?? `Checked ${new Date(result.checkedAt).toLocaleTimeString()}.`}
                        </Callout>
                      ) : conn.healthCheckedAt ? (
                        <HelpText>
                          Last checked {new Date(conn.healthCheckedAt).toLocaleString()} — we check
                          every six hours on our own.
                        </HelpText>
                      ) : null}

                      {/* ⚠️ **The permissions this connection is MISSING, by name, beside the button
                          that fixes it.** A badge reading "limited" leaves a client unable to act,
                          because the remedy is re-approving one specific box.

                          ⚠️ **Rendered only for a non-empty list.** `null` means the provider never
                          stated its grant (B-278) — unknown, not "granted nothing" — and printing a
                          warning from a value we failed to read would tell a client their connection
                          is broken on the strength of our own gap.

                          ⚠️ **This is the state every existing connection lands in when we add a
                          required scope**, since a token refresh reissues the SAME grant and only a
                          re-consent can widen it. Nothing else in the product would have said so. */}
                      {conn.missingReadScopes && conn.missingReadScopes.length > 0 ? (
                        <Callout tone="warning" title="Some permissions were never approved">
                          <p>
                            This connection is missing{' '}
                            <code>{conn.missingReadScopes.join(' ')}</code>. Anything needing those
                            permissions has no data — everything else keeps working.
                          </p>
                          <p>
                            Reauthorise below and approve them. It replaces this connection&rsquo;s
                            permissions rather than creating a second one, so nothing you have already
                            synced is disturbed.
                          </p>
                        </Callout>
                      ) : null}

                      {/* ⚠️ **The outcome of THIS row's action, next to the button that caused it**
                          (B-297). Rendered above the actions rather than below, so a reader whose eye
                          is on the button they just pressed sees the answer without moving. */}
                      {notice?.target === conn.id ? (
                        <div aria-live="polite">
                          <Callout
                            tone={notice.tone}
                            title={notice.tone === 'success' ? 'Done' : 'That did not complete'}
                          >
                            {notice.text}
                          </Callout>
                        </div>
                      ) : null}

                      <div className="web-integrations__actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={testing === conn.id}
                          onClick={() => void test(conn.id)}
                        >
                          {testing === conn.id ? 'Checking…' : 'Test this connection'}
                        </Button>
                        {/* ⚠️ **This button did two things wrong and its own comment described a
                            third.** It navigated top-level to `/api/relay/connect/…`, which carries
                            no `Authorization` header — and `handleConnect` resolves the caller
                            through `callerOrgs`, which reads only that header — so it answered 401
                            and put a JSON problem document in the address bar where a consent
                            screen belonged. And it sent no `escalate`, so `connect.ts` took
                            `crypto.randomUUID()` and minted a *second* connection, orphaning every
                            source attached to the first — the exact opposite of what the comment
                            claimed. `startConnect` does both correctly, once, for every caller. */}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void begin(provider.providerId, conn.id)}
                        >
                          Reauthorise
                        </Button>
                        {/* ⚠️ **This did nothing at all.** It navigated to
                            `#/sources?disconnect=<id>` and nothing in `Sources` has ever read that
                            parameter — so the one control a worried client reaches for was inert,
                            while `DELETE /api/relay/connections/:id` sat built and tested. */}
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={disconnecting === conn.id}
                          onClick={() => void disconnect(conn.id, provider.providerName)}
                        >
                          {disconnecting === conn.id ? 'Disconnecting…' : 'Disconnect'}
                        </Button>
                      </div>
                      {/* Per connection, like every other control here: two Salesforce orgs have two
                          schemas, and a field added in one says nothing about the other. */}
                      <SchemaPanel
                        connectionId={conn.id}
                        providerId={provider.providerId}
                        objects={provider.objects ?? []}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        );
      })}
    </div>
  );
}
